/* Progressive enhancement of native, independently usable product links. */
if (!customElements.get('rga-swipe-stack')) {
  customElements.define('rga-swipe-stack', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.slides = [...this.querySelectorAll('.rga-stack__slide')];
      if (this.slides.length < 2) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.index = 0;
      this.busy = false;
      this.animations = [];
      this.viewportWidth = window.innerWidth;
      this.preference = matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelectorAll('img').forEach(image => { image.draggable = false; });
      this.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.addEventListener('pointerdown', event => this.startDrag(event), { signal });
      window.addEventListener('pointermove', event => this.moveDrag(event), { signal, passive: false });
      window.addEventListener('pointerup', event => this.endDrag(event), { signal });
      window.addEventListener('pointercancel', event => {
        if (event.pointerId === this.drag?.id) this.cancelDrag();
      }, { signal });
      this.addEventListener('lostpointercapture', event => {
        if (event.target === this && event.pointerId === this.drag?.id) this.cancelDrag();
      }, { signal });
      this.addEventListener('click', event => {
        if (performance.now() < this.suppressClickUntil && event.target.closest('.rga-stack__slides')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.suppressClickUntil = 0;
        }
      }, { signal, capture: true });
      this.querySelectorAll('[data-swipe-direction]').forEach(button => {
        button.addEventListener('click', () => this.go(Number(button.dataset.swipeDirection)), { signal });
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.drag) { this.cancelDrag(); return; }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.go(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.preference.addEventListener('change', () => {
        this.cancelDrag();
        this.animations.forEach(animation => animation.cancel());
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.cancelDrag();
          this.animations.forEach(animation => animation.cancel());
        }
      }, { signal });
      window.addEventListener('resize', () => {
        if (window.innerWidth === this.viewportWidth) return;
        this.viewportWidth = window.innerWidth;
        this.cancelDrag();
      }, { signal, passive: true });
      window.addEventListener('blur', () => this.cancelDrag(), { signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.contains(event.target));
        if (index >= 0 && !this.busy) {
          this.index = index;
          this.layout();
          this.announce();
        }
      }, { signal });
      this.layout();
      this.announce();
      this.dataset.ready = '';
    }
    layout(direction = 1) {
      this.slides.forEach((slide, index) => {
        slide.dataset.depth = String(((index - this.index) * direction + this.slides.length) % this.slides.length);
        slide.inert = index !== this.index;
        if (index === this.index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
    }
    announce() {
      this.dataset.activeIndex = String(this.index);
      this.querySelector('.rga-stack__count').textContent = `${this.index + 1} / ${this.slides.length}`;
      this.querySelector('[data-swipe-status]').textContent = this.dataset.positionLabel
        .replace('__current__', String(this.index + 1)).replace('__total__', String(this.slides.length))
        .replace('__title__', this.slides[this.index].dataset.title);
    }
    startDrag(event) {
      if (!event.isPrimary) { this.cancelDrag(); return; }
      this.suppressClickUntil = 0;
      if (this.busy || this.drag || (event.pointerType === 'mouse' && event.button !== 0) ||
          event.target.closest('button, rosarium-wishlist-action')) return;
      const slide = event.target.closest('.rga-stack__slide[data-depth="0"]');
      if (!slide) return;
      this.drag = { id: event.pointerId, slide, x: event.clientX, y: event.clientY, dx: 0,
        started: false, samples: [{ x: 0, at: event.timeStamp }], direction: 0,
        width: slide.getBoundingClientRect().width };
    }
    moveDrag(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.started) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) * 1.15) { this.releaseDrag(); return; }
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy) * 1.15) return;
        drag.started = true;
        try { this.setPointerCapture(event.pointerId); }
        catch { this.cancelDrag(); return; }
        this.dataset.dragging = '';
        drag.slide.style.willChange = 'transform';
      }
      if (event.cancelable) event.preventDefault();
      drag.dx = dx;
      const now = event.timeStamp;
      drag.samples.push({ x: dx, at: now });
      while (drag.samples.length > 2 && drag.samples[1].at < now - 100) drag.samples.shift();
      const direction = dx < 0 ? 1 : -1;
      if (direction !== drag.direction) {
        drag.direction = direction;
        this.layout(direction);
      }
      const ratio = Math.max(-1.2, Math.min(1.2, dx / drag.width));
      const lift = this.preference.matches ? 0 : -Math.abs(ratio) * 14;
      const tilt = this.preference.matches ? 0 : ratio * 11;
      drag.slide.style.transform = `translate3d(${ratio * drag.width}px, ${lift}px, 0) rotate(${tilt}deg)`;
    }
    releaseDrag() {
      const drag = this.drag;
      this.drag = null;
      delete this.dataset.dragging;
      if (drag && this.hasPointerCapture(drag.id)) this.releasePointerCapture(drag.id);
      if (drag?.started) this.suppressClickUntil = performance.now() + 500;
      return drag;
    }
    endDrag(event) {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      const drag = this.drag;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.started && Math.abs(dx) >= 8 && Math.abs(dx) > Math.abs(dy) * 1.15) drag.started = true;
      if (drag.started) drag.dx = dx;
      this.releaseDrag();
      if (!drag.started) return;
      const distance = Math.abs(drag.dx), now = event.timeStamp;
      if (drag.dx !== drag.samples[drag.samples.length - 1].x) drag.samples.push({ x: drag.dx, at: now });
      const last = drag.samples[drag.samples.length - 1];
      const recent = drag.samples.find(sample => sample.at >= last.at - 100 && sample.at < last.at) || last;
      const velocity = now - last.at <= 100 ? (last.x - recent.x) / Math.max(1, last.at - recent.at) : 0;
      const speed = Math.sign(velocity) === Math.sign(drag.dx) ? Math.abs(velocity) : 0;
      if (distance >= 36 || (distance >= 18 && speed >= .15)) this.go(drag.dx < 0 ? 1 : -1);
      else this.restore(drag.slide);
    }
    cancelDrag() {
      const drag = this.releaseDrag();
      if (drag?.started) this.restore(drag.slide);
    }
    async restore(slide) {
      const connection = this.abort;
      this.busy = true;
      const transform = getComputedStyle(slide).transform;
      slide.style.removeProperty('transform');
      if (!this.preference.matches && !document.hidden && slide.animate) {
        this.animations = [slide.animate([{ transform }, { transform: 'none' }],
          { duration: 180, easing: 'cubic-bezier(.16,1,.3,1)' })];
        await Promise.allSettled(this.animations.map(animation => animation.finished));
      }
      if (this.abort !== connection || !this.isConnected) return;
      slide.style.removeProperty('will-change');
      this.animations = [];
      this.busy = false;
      if (this.isConnected) this.layout();
    }
    async go(direction) {
      if (this.busy || this.drag) return;
      const connection = this.abort;
      this.busy = true;
      this.dataset.swiping = '';
      const outgoing = this.slides[this.index];
      const next = (this.index + direction + this.slides.length) % this.slides.length;
      const incoming = this.slides[next];
      const transferFocus = outgoing.contains(document.activeElement);
      this.layout(direction);
      const start = getComputedStyle(outgoing).transform;
      const behind = getComputedStyle(incoming).transform;
      outgoing.style.removeProperty('transform');
      if (!this.preference.matches && !document.hidden && outgoing.animate) {
        const width = outgoing.getBoundingClientRect().width;
        outgoing.style.willChange = incoming.style.willChange = 'transform';
        this.animations = [
          outgoing.animate([{ transform: start, opacity: 1 },
            { transform: `translate3d(${-direction * width * 1.2}px, -18px, 0) rotate(${-direction * 12}deg)`, opacity: 0 }],
            { duration: 220, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'forwards' }),
          incoming.animate([{ transform: behind }, { transform: 'none' }],
            { duration: 260, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'forwards' })
        ];
        await Promise.allSettled(this.animations.map(animation => animation.finished));
      }
      if (this.abort !== connection || !this.isConnected) return;
      this.index = next;
      this.layout();
      this.animations.forEach(animation => animation.cancel());
      this.animations = [];
      outgoing.style.removeProperty('will-change');
      incoming.style.removeProperty('will-change');
      this.busy = false;
      delete this.dataset.swiping;
      if (!this.isConnected) return;
      this.announce();
      if (transferFocus) incoming.querySelector('.rga-card__link').focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.releaseDrag();
      this.abort?.abort();
      this.animations?.forEach(animation => animation.cancel());
      this.slides?.forEach(slide => {
        slide.inert = false;
        slide.removeAttribute('aria-hidden');
        slide.style.removeProperty('transform');
        slide.style.removeProperty('will-change');
      });
      this.abort = null;
      delete this.dataset.ready;
    }
  });
}
if (!customElements.get('rga-discovery')) {
  customElements.define('rga-discovery', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.activeIndex = 0;
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), { signal });
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            this.select(next);
            this.tabs[next].focus({ preventScroll: true });
          }
        }, { signal });
      });
      this.querySelectorAll('.rga-rail').forEach(rail => {
        const panel = rail.closest('.rga-panel');
        rail.addEventListener('scroll', () => this.updateControls(panel), { passive: true, signal });
        panel.querySelectorAll('[data-direction]').forEach(button => {
          button.addEventListener('click', () => {
            const card = rail.firstElementChild;
            const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
            const step = card ? card.getBoundingClientRect().width + gap : rail.clientWidth;
            rail.scrollBy({ left: (button.dataset.direction === 'next' ? 1 : -1) * step,
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          }, { signal });
        });
      });
      this.observer = new ResizeObserver(() => this.updateControls(this.panels[this.activeIndex]));
      this.observer.observe(this);
      this.select(0);
      this.dataset.ready = '';
      this.addEventListener('shopify:block:select', event => {
        const id = event.detail?.blockId;
        const index = this.tabs.findIndex(tab => tab.id.endsWith('-' + id));
        if (index >= 0) this.select(index);
      }, { signal });
    }
    select(index) {
      this.activeIndex = index;
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
      this.updateControls(this.panels[index]);
    }
    updateControls(panel) {
      if (!panel) return;
      const rail = panel.querySelector('.rga-rail');
      const max = rail.scrollWidth - rail.clientWidth;
      panel.querySelector('[data-direction="previous"]').disabled = rail.scrollLeft <= 2;
      panel.querySelector('[data-direction="next"]').disabled = rail.scrollLeft >= max - 2;
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.abort = null;
    }
  });
}

if (!customElements.get('rga-product-rail')) {
  customElements.define('rga-product-rail', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.rail = this.querySelector('.rga-rail');
      if (!this.rail) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.previous = this.querySelector('[data-direction="previous"]');
      this.next = this.querySelector('[data-direction="next"]');
      this.rail.addEventListener('scroll', () => this.updateControls(), { passive: true, signal });
      this.querySelectorAll('[data-direction]').forEach(button => {
        button.addEventListener('click', () => {
          const card = this.rail.firstElementChild;
          const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
          const step = card ? card.getBoundingClientRect().width + gap : this.rail.clientWidth;
          this.rail.scrollBy({
            left: (button.dataset.direction === 'next' ? 1 : -1) * step,
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
          });
        }, { signal });
      });
      this.observer = new ResizeObserver(() => this.updateControls());
      this.observer.observe(this.rail);
      this.updateControls();
      this.dataset.ready = '';
    }
    updateControls() {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      if (this.previous) this.previous.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.rail.scrollLeft >= max - 2;
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.abort = null;
      delete this.dataset.ready;
    }
  });
}

if (!customElements.get('rga-bouquet-scene')) {
  customElements.define('rga-bouquet-scene', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.motions = [...this.querySelectorAll('.rga-scene__motion')];
      this.animations = [];
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this.abort.signal;
      this.motionPreference.addEventListener('change', () => {
        if (this.motionPreference.matches) this.finish();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.finish();
      }, { signal });
      if (this.dataset.sceneEntered || this.motionPreference.matches || document.hidden || !('IntersectionObserver' in window) || !Element.prototype.animate || !this.canBlendPresentation()) {
        this.finish();
        return;
      }
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.3)) this.enter();
      }, { threshold: 0.3, rootMargin: '0px' });
      this.observer.observe(this);
      this.dataset.sceneState = 'ready';
    }
    canBlendPresentation() {
      return window.CSS?.supports('mix-blend-mode', 'multiply') && this.motions.length > 0 && this.motions.every(motion => motion.querySelector('[data-autumn-blend] img'));
    }
    waitForPresentation() {
      const images = [...this.querySelectorAll('.rga-scene__motion img')];
      if (!images.length || !this.canBlendPresentation()) return Promise.resolve(false);
      return new Promise(resolve => {
        const signal = this.abort.signal;
        let settled = false;
        let decoded = false;
        let timer;
        const finish = ready => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          images.forEach(image => {
            image.removeEventListener('load', check);
            image.removeEventListener('error', check);
          });
          signal.removeEventListener('abort', abort);
          resolve(ready);
        };
        const check = () => {
          if (decoded && images.every(image => image.complete)) {
            finish(images.every(image => image.naturalWidth > 0));
          }
        };
        const abort = () => finish(false);
        images.forEach(image => {
          image.addEventListener('load', check);
          image.addEventListener('error', check);
        });
        signal.addEventListener('abort', abort, { once: true });
        timer = setTimeout(() => finish(false), 1000);
        Promise.all(images.map(image => Promise.resolve().then(() => image.decode ? image.decode() : null))).then(() => {
          decoded = true;
          check();
        }, () => finish(false));
        check();
      });
    }
    async enter() {
      if (this.dataset.sceneEntered) return;
      this.dataset.sceneEntered = 'true';
      this.dataset.sceneState = 'loading';
      this.observer?.disconnect();
      const ready = await this.waitForPresentation();
      const rect = this.getBoundingClientRect();
      if (!ready || !this.isConnected || this.motionPreference.matches || document.hidden || this.dataset.sceneState === 'complete' || rect.bottom <= 0 || rect.top >= innerHeight) {
        this.finish();
        return;
      }
      this.dataset.sceneState = 'animating';
      this.animations = this.motions.map((motion, index) => {
        motion.style.willChange = 'transform';
        return motion.animate([
          { transform: `translate3d(${index % 2 === 0 ? -56 : 56}px, 64px, 0) rotate(${index % 2 === 0 ? -7 : 7}deg) scale(.96)` },
          { transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)' }
        ], { duration: 700, delay: index * 90, easing: 'cubic-bezier(.16, 1, .3, 1)', fill: 'backwards' });
      });
      Promise.allSettled(this.animations.map(animation => animation.finished)).then(() => this.finish());
    }
    finish() {
      this.observer?.disconnect();
      this.animations?.forEach(animation => animation.cancel());
      this.animations = [];
      this.motions?.forEach(motion => motion.style.removeProperty('will-change'));
      this.dataset.sceneEntered = 'true';
      this.dataset.sceneState = 'complete';
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.finish();
      this.abort = null;
    }
  });
}
